import { useEffect, useRef, useState } from "react";

/**
 * Input de cores (paleta) — aceita hex digitado (#fe7000 ou #f70) OU um seletor
 * de cor nativo, e mostra mensagem de erro quando o hex é inválido (antes o
 * valor errado era ignorado em silêncio e ainda "contava" como texto).
 *
 * Compartilhado entre o cadastro de produto (Identidade visual) e o onboarding
 * (step MIV). Valor = array de "#rrggbb".
 */
export function ColorChipsInput({
  value,
  onChange,
  placeholder = "#fe7000, #0d1526...",
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const normalizeHex = (raw: string): string | null => {
    const t = raw.trim().replace(/^#?/, "");
    if (/^[0-9a-fA-F]{3}$/.test(t)) {
      return `#${t.split("").map((c) => c + c).join("").toLowerCase()}`;
    }
    if (/^[0-9a-fA-F]{6}$/.test(t)) return `#${t.toLowerCase()}`;
    return null;
  };

  const add = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const hex = normalizeHex(trimmed);
    if (!hex) {
      setError("Cor inválida. Use um hex como #fe7000 (6 dígitos) ou #f70 (3) — ou pegue no seletor.");
      return;
    }
    if (value.includes(hex)) {
      setError("Essa cor já está na lista.");
      setDraft("");
      return;
    }
    setError(null);
    onChange([...value, hex]);
    setDraft("");
  };
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  // Seletor de cor nativo: o React mapeia `onChange` do <input type="color"> pro
  // evento "input", que dispara a cada passo do arraste dentro do quadro RGB — aí
  // TODA cor por onde o mouse passou entrava na paleta. Ouvimos o evento nativo
  // "change" (só dispara quando o usuário CONFIRMA a cor) e adicionamos uma vez.
  const addRef = useRef(add);
  addRef.current = add;
  const colorInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;
    const onCommit = () => addRef.current(el.value);
    el.addEventListener("change", onCommit);
    return () => el.removeEventListener("change", onCommit);
  }, []);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          padding: 8,
          minHeight: 44,
          border: `1px solid ${error ? "#e5484d" : "var(--wf-ink-5)"}`,
          borderRadius: 8,
          background: "var(--wf-paper)",
          alignItems: "center",
        }}
      >
        {value.map((hex, i) => (
          <span
            key={`${hex}-${i}`}
            onClick={() => remove(i)}
            title="Remover"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 8px 3px 4px",
              borderRadius: 999,
              background: "var(--wf-paper)",
              border: "1px solid var(--wf-ink-5)",
              fontSize: 12,
              fontFamily: "monospace",
              cursor: "pointer",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                background: hex,
                border: "1px solid rgba(0,0,0,0.12)",
              }}
            />
            <span style={{ color: "var(--wf-ink)" }}>{hex}</span>
            <span style={{ color: "var(--wf-ink-3)" }}>×</span>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && value.length > 0) {
              remove(value.length - 1);
            }
          }}
          onBlur={() => draft.trim() && add(draft)}
          placeholder={value.length === 0 ? placeholder : ""}
          style={{
            flex: 1,
            minWidth: 90,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 13,
            fontFamily: "monospace",
          }}
        />
        {/* Seletor de cor nativo — adiciona a cor escolhida direto na paleta. */}
        <label
          title="Escolher cor"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 8,
            border: "1px dashed var(--wf-ink-4)",
            cursor: "pointer",
            flexShrink: 0,
            fontSize: 14,
            color: "var(--wf-ink-3)",
          }}
        >
          +
          <input
            ref={colorInputRef}
            type="color"
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
          />
        </label>
      </div>
      {error && (
        <p style={{ fontSize: 11.5, color: "#e5484d", margin: "6px 2px 0", fontWeight: 500 }}>{error}</p>
      )}
    </div>
  );
}
